import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  MailIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  PaperAirplaneIcon
} from '@heroicons/react/outline';

const EmailSettings = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  
  const [config, setConfig] = useState({
    host: '',
    port: 587,
    secure: false,
    user: '',
    pass: '',
    fromName: '',
    fromEmail: '',
    replyTo: '',
    adminEmails: []
  });
  
  const [testEmail, setTestEmail] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  const [reportConfig, setReportConfig] = useState({
    recipientType: 'COMPANY',
    recipientId: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/emails/config');
      setConfig({
        host: response.data.smtp.host || '',
        port: response.data.smtp.port || 587,
        secure: response.data.smtp.secure || false,
        user: response.data.smtp.user || '',
        pass: '', // Never send password from server
        fromName: response.data.from.name || '',
        fromEmail: response.data.from.email || '',
        replyTo: response.data.replyTo || '',
        adminEmails: response.data.adminEmails || []
      });
    } catch (error) {
      console.error('Error fetching email config:', error);
      toast.error(t('email.fetchConfigError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const updateData = {
        host: config.host,
        port: parseInt(config.port),
        secure: config.secure,
        user: config.user,
        fromName: config.fromName,
        fromEmail: config.fromEmail,
        replyTo: config.replyTo,
        adminEmails: config.adminEmails
      };
      
      // Only include password if it was changed
      if (config.pass) {
        updateData.pass = config.pass;
      }
      
      const response = await api.put('/api/emails/config', updateData);
      
      setConnectionStatus(response.data.isValid);
      toast.success(t('email.configSaved'));
      
      if (!response.data.isValid) {
        toast.warning(t('email.configSavedButInvalid'));
      }
    } catch (error) {
      console.error('Error saving email config:', error);
      toast.error(error.response?.data?.error || t('email.saveConfigError'));
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyConnection = async () => {
    try {
      setVerifying(true);
      const response = await api.post('/api/emails/verify-connection');
      
      setConnectionStatus(response.data.isValid);
      
      if (response.data.isValid) {
        toast.success(t('email.connectionSuccess'));
      } else {
        toast.error(t('email.connectionFailed'));
      }
    } catch (error) {
      console.error('Error verifying connection:', error);
      setConnectionStatus(false);
      toast.error(error.response?.data?.details || t('email.connectionError'));
    } finally {
      setVerifying(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error(t('email.enterTestEmail'));
      return;
    }
    
    try {
      setTesting(true);
      await api.post('/api/emails/test', { to: testEmail, message: 'This is a test email from the admin panel.' });
      toast.success(t('email.testEmailSent'));
      setTestEmail('');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.details || t('email.testEmailError'));
    } finally {
      setTesting(false);
    }
  };

  const handleSendMonthlyReport = async () => {
    if (!reportConfig.recipientId) {
      toast.error('Please enter a recipient ID');
      return;
    }
    
    try {
      setSendingReport(true);
      const response = await api.post('/api/emails/send-monthly-report', reportConfig);
      toast.success(response.data.message);
      console.log('Report sent:', response.data.details);
    } catch (error) {
      console.error('Error sending monthly report:', error);
      toast.error(error.response?.data?.details || 'Failed to send monthly report');
    } finally {
      setSendingReport(false);
    }
  };

  const handleAddAdminEmail = () => {
    setConfig(prev => ({
      ...prev,
      adminEmails: [...prev.adminEmails, '']
    }));
  };

  const handleRemoveAdminEmail = (index) => {
    setConfig(prev => ({
      ...prev,
      adminEmails: prev.adminEmails.filter((_, i) => i !== index)
    }));
  };

  const handleAdminEmailChange = (index, value) => {
    setConfig(prev => ({
      ...prev,
      adminEmails: prev.adminEmails.map((email, i) => i === index ? value : email)
    }));
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <MailIcon className="w-8 h-8" />
          {t('email.settings')}
        </h1>
        <p className="text-gray-600 mt-1">{t('email.settingsDescription')}</p>
      </div>

      {/* Connection Status */}
      {connectionStatus !== null && (
        <div className={`mb-6 p-4 rounded-lg border-2 ${
          connectionStatus 
            ? 'bg-green-50 border-green-500' 
            : 'bg-red-50 border-red-500'
        }`}>
          <div className="flex items-center gap-2">
            {connectionStatus ? (
              <>
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
                <span className="text-green-800 font-semibold">{t('email.connectionValid')}</span>
              </>
            ) : (
              <>
                <XCircleIcon className="w-6 h-6 text-red-600" />
                <span className="text-red-800 font-semibold">{t('email.connectionInvalid')}</span>
              </>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* SMTP Configuration */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <CogIcon className="w-5 h-5" />
            {t('email.smtpConfig')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('email.smtpHost')} *
              </label>
              <input
                type="text"
                value={config.host}
                onChange={(e) => setConfig({ ...config, host: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="smtp.gmail.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('email.smtpPort')} *
              </label>
              <input
                type="number"
                value={config.port}
                onChange={(e) => setConfig({ ...config, port: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="flex items-center pt-7">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.secure}
                  onChange={(e) => setConfig({ ...config, secure: e.target.checked })}
                  className="rounded text-blue-600"
                />
                <span className="text-sm font-medium text-gray-700">{t('email.useSSL')}</span>
              </label>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('email.username')} *
              </label>
              <input
                type="text"
                value={config.user}
                onChange={(e) => setConfig({ ...config, user: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="your-email@domain.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('email.password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={config.pass}
                  onChange={(e) => setConfig({ ...config, pass: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={t('email.passwordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? t('common.hide') : t('common.show')}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('email.passwordNote')}</p>
            </div>
          </div>
          
          <div className="mt-4">
            <button
              type="button"
              onClick={handleVerifyConnection}
              disabled={verifying || !config.host || !config.user}
              className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400"
            >
              {verifying ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t('email.verifying')}
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  {t('email.verifyConnection')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Email Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {t('email.emailSettings')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('email.fromName')} *
              </label>
              <input
                type="text"
                value={config.fromName}
                onChange={(e) => setConfig({ ...config, fromName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Insurance System"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('email.fromEmail')} *
              </label>
              <input
                type="email"
                value={config.fromEmail}
                onChange={(e) => setConfig({ ...config, fromEmail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="reports@insurance.qirat.ly"
                required
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('email.replyTo')}
              </label>
              <input
                type="email"
                value={config.replyTo}
                onChange={(e) => setConfig({ ...config, replyTo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="support@insurance.qirat.ly"
              />
            </div>
          </div>
        </div>

        {/* Admin Emails */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t('email.adminEmails')}
          </h2>
          <p className="text-sm text-gray-600 mb-4">{t('email.adminEmailsDescription')}</p>
          
          <div className="space-y-2">
            {config.adminEmails.map((email, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleAdminEmailChange(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="admin@company.com"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveAdminEmail(index)}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  {t('common.remove')}
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddAdminEmail}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + {t('email.addAdminEmail')}
            </button>
          </div>
        </div>

        {/* Test Email */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <PaperAirplaneIcon className="w-5 h-5" />
            {t('email.sendTest')}
          </h2>
          
          <div className="flex gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={t('email.testEmailPlaceholder')}
            />
            <button
              type="button"
              onClick={handleSendTest}
              disabled={testing || !testEmail}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
            >
              {testing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t('email.sending')}
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-5 h-5" />
                  {t('email.send')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Manual Monthly Report Trigger */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <MailIcon className="w-5 h-5" />
            📊 Send Monthly Report (Manual)
          </h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Type
              </label>
              <select
                value={reportConfig.recipientType}
                onChange={(e) => setReportConfig(prev => ({ ...prev, recipientType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="COMPANY">Company</option>
                <option value="BRANCH">Branch</option>
                <option value="GOVERNING_BODY">Governing Body</option>
                <option value="USER">User</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient ID
              </label>
              <input
                type="number"
                value={reportConfig.recipientId}
                onChange={(e) => setReportConfig(prev => ({ ...prev, recipientId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter ID"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <input
                type="number"
                value={reportConfig.year}
                onChange={(e) => setReportConfig(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="2020"
                max="2100"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Month
              </label>
              <select
                value={reportConfig.month}
                onChange={(e) => setReportConfig(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
          </div>
          
          <button
            type="button"
            onClick={handleSendMonthlyReport}
            disabled={sendingReport || !reportConfig.recipientId}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 font-semibold"
          >
            {sendingReport ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Sending Report...
              </>
            ) : (
              <>
                <MailIcon className="w-5 h-5" />
                Send Monthly Report Now
              </>
            )}
          </button>
          
          <p className="mt-3 text-sm text-gray-600">
            💡 <strong>Tip:</strong> This will generate and send the monthly report for the selected recipient immediately, regardless of scheduled settings.
          </p>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-semibold"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {t('common.saving')}
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                {t('common.saveChanges')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailSettings;

